import { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useAnchoredMenu } from "../components/useAnchoredMenu";
import { bookingStatusClass, bookingStatusLabel } from "./status";

interface BookingStatusBadgeProps {
  status: string;
  /** Statuses the booking may change to (the current one is shown ticked) */
  options: string[];
  onChange: (newStatus: string) => Promise<void> | void;
  disabled?: boolean;
}

/** Room booking status pill; tap it to change the status (same pattern as InlineStatusBadge). */
export default function BookingStatusBadge({ status, options, onChange, disabled }: BookingStatusBadgeProps) {
  const [loading, setLoading] = useState(false);
  const all = [status, ...options.filter((o) => o !== status)];
  const { open, setOpen, toggle, anchorRef, menuRef, style } = useAnchoredMenu(all.length * 40 + 36);
  const canChange = !disabled && all.length > 1;

  const handleChange = async (next: string) => {
    setOpen(false);
    if (next === status) return;
    setLoading(true);
    try { await onChange(next); } finally { setLoading(false); }
  };

  return (
    <div className="relative inline-block">
      <button
        ref={anchorRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); if (canChange) toggle(); }}
        disabled={!canChange || loading}
        aria-haspopup={canChange ? "menu" : undefined}
        aria-expanded={canChange ? open : undefined}
        aria-label={canChange ? `Status: ${bookingStatusLabel(status)}. Change status` : `Status: ${bookingStatusLabel(status)}`}
        className={`inline-flex items-center gap-1 min-h-8 md:min-h-0 px-2.5 py-1 md:px-2 md:py-0.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${bookingStatusClass(status)} ${
          canChange ? "cursor-pointer hover:opacity-80 hover:shadow-sm" : "cursor-default"
        } ${loading ? "opacity-60" : ""}`}
      >
        {loading ? (
          <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          bookingStatusLabel(status)
        )}
        {canChange && !loading && <ChevronDown className="w-2.5 h-2.5 opacity-60" />}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          style={style}
          onClick={(e) => e.stopPropagation()}
          className="bg-white border border-stone-200 rounded-xl shadow-xl overflow-hidden min-w-[170px]"
        >
          <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-stone-400">Change to</div>
          {all.map((opt) => (
            <button
              key={opt}
              type="button"
              role="menuitem"
              onClick={() => handleChange(opt)}
              className="w-full min-h-10 flex items-center justify-between gap-2 px-3 py-2 text-xs text-left hover:bg-stone-50 transition-colors"
            >
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${bookingStatusClass(opt)}`}>
                {bookingStatusLabel(opt)}
              </span>
              {opt === status && <Check className="w-3 h-3 text-amber-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
